/**
 * Iter56 Iter B — Provider-neutral measurement-provider selector.
 *
 * The frontend workspace (built in Iter C) will call `resolveMeasurementProvider()`
 * once at mount to decide whether to use the Google workspace or the existing
 * Mapbox workspace. Selection rules, in order:
 *
 *   1. Backend `/api/measurements/config.google_map_enabled` MUST be true.
 *      This is the authoritative source of truth — the frontend env var
 *      is only a hint. If the backend flag is false, we always pick "mapbox".
 *   2. A non-empty browser key MUST resolve via getGoogleMapsBrowserKey().
 *      Without a key we cannot load Maps JS, so we fall back to Mapbox even
 *      if the flag is on.
 *   3. Otherwise we pick "google".
 *
 * The Mapbox path is the default and unchanged. The full Google workspace is
 * NOT built in this scaffold — this file only exposes the selection contract
 * so components can subscribe safely without probing env vars themselves.
 */

import { api } from "./api";
import { getGoogleMapsBrowserKey, googleMeasurementMapEnabled } from "./googleMapsKey";

export const PROVIDER_MAPBOX = "mapbox";
export const PROVIDER_GOOGLE = "google";

// Cached across the session — a page reload always re-fetches.
let _cache = null;

export async function fetchMeasurementConfig({ force = false } = {}) {
  if (_cache && !force) return _cache;
  try {
    const { data } = await api.get("/measurements/config");
    _cache = data || {};
  } catch (e) {
    // If the backend is unreachable or the endpoint 401s, fall back safely.
    _cache = { google_map_enabled: false, google_solar_enabled: false, mapbox_enabled: true };
  }
  return _cache;
}

export async function resolveMeasurementProvider({ force = false } = {}) {
  const cfg = await fetchMeasurementConfig({ force });
  const backendEnabled = !!cfg.google_map_enabled;
  const frontendEnabled = googleMeasurementMapEnabled();
  const key = getGoogleMapsBrowserKey();
  const canUseGoogle = backendEnabled && frontendEnabled && !!key;
  return {
    provider: canUseGoogle ? PROVIDER_GOOGLE : PROVIDER_MAPBOX,
    google_solar_enabled: !!cfg.google_solar_enabled,
    reason: canUseGoogle
      ? "google_enabled"
      : !backendEnabled
      ? "backend_flag_off"
      : !frontendEnabled
      ? "frontend_flag_off"
      : !key
      ? "browser_key_missing"
      : "unknown",
  };
}

// Test-only helper to reset the session cache between unit tests.
export function __resetMeasurementProviderCache() {
  _cache = null;
}
